import type { ReactNode } from "react"
import { Head, Link, usePage } from "@inertiajs/react"
import { ExternalLink } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { MapCanvas } from "@/components/MapCanvas"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { VerificationQuickAdd, type VerificationFormProps } from "@/components/VerificationQuickAdd"
import { PhotoManager, type PhotoManagerProps } from "@/components/PhotoManager"

import type { PageProps } from "@/types/inertia"
import type { PlaceFormRecord } from "./Form"

type PlaceDetail = PlaceFormRecord & {
  id: number
  destination_name: string | null
  category_label: string | null
  category_key: string | null
  updated_at: string | null
}

const OPENING_HOURS_DAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const

const BEACH_BOOLEAN_FACTS = [
  "dogs_allowed",
  "fkk_allowed",
  "kid_friendly",
  "showers",
] as const

const GENERAL_BOOLEAN_FACTS = [
  "wheelchair_accessible",
  "toilets",
  "food_nearby",
  "parking_nearby",
] as const

export default function AdminPlacesShow({
  place,
  verification_form,
  photo_manager,
}: {
  place: PlaceDetail
  verification_form: VerificationFormProps
  photo_manager: PhotoManagerProps
}) {
  const { props } = usePage<PageProps>()
  const title = place.name_en ?? place.name_de ?? `Place #${place.id}`
  const description = `Admin details and verification history for ${title}.`
  const hasCoordinates = place.latitude !== null && place.longitude !== null

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <AdminShell>
        <div className="border-b border-hairline pb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1>{title}</h1>
              <p className="mt-1">
                {place.category_label ?? "Uncategorized"} in {place.destination_name ?? "Unknown destination"}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge tone={place.status === "published" ? "accent" : "muted"}>{titleize(place.status)}</Badge>
              <Button asChild variant="secondary"><Link href={`/admin/places/${place.id}/edit`}>Edit</Link></Button>
            </div>
          </div>
        </div>

        <Section title="Publishing">
          <DetailList>
            <DetailRow label="Destination" value={place.destination_name} />
            <DetailRow label="Category" value={place.category_label} />
            <DetailRow label="Status" value={titleize(place.status)} />
            <DetailRow label="Updated" value={place.updated_at ? formatDateTime(place.updated_at) : null} />
          </DetailList>
        </Section>

        <Section title="Localized content">
          <div className="grid gap-4 lg:grid-cols-2">
            <LocalizedContent locale="English" name={place.name_en} slug={place.slug_en} summary={place.summary_en} body={place.body_en} />
            <LocalizedContent locale="German" name={place.name_de} slug={place.slug_de} summary={place.summary_de} body={place.body_de} />
          </div>
        </Section>

        <Section title="Search appearance" description="Explicit search metadata and the structured-data entity used on the public page.">
          <DetailList>
            <DetailRow label="Search title EN" value={place.meta_title_en} />
            <DetailRow label="Search title DE" value={place.meta_title_de} />
            <DetailRow label="Search description EN" value={place.meta_description_en} multiline />
            <DetailRow label="Search description DE" value={place.meta_description_de} multiline />
            <DetailRow label="Schema type" value={place.schema_type} />
          </DetailList>
        </Section>

        <Section title="Location and links">
          {hasCoordinates && (
            <MapCanvas
              maptilerKey={props.maptiler_key}
              center={[place.longitude as number, place.latitude as number]}
              zoom={13}
              marker
              className="mb-5 h-[22rem]"
            />
          )}
          <DetailList>
            <DetailRow label="Center" value={hasCoordinates ? `${place.latitude}, ${place.longitude}` : null} />
            <DetailRow label="Address" value={place.address} />
            <DetailRow label="Phone" value={place.phone ? <a href={`tel:${place.phone}`}>{place.phone}</a> : null} />
            <DetailRow label="Website URL" value={<ExternalUrl url={place.website_url} />} />
            <DetailRow label="Booking URL" value={<ExternalUrl url={place.booking_url} />} />
          </DetailList>
        </Section>

        <Section title="Facts" description="Unknown values are shown explicitly so missing information is easy to spot.">
          {place.category_key === "beach" && (
            <FactGroup title="Beach details">
              <DetailList>
                <DetailRow label="Beach material" value={humanizeValue(place.beach_material)} />
                <DetailRow label="Sea floor material" value={humanizeValue(place.sea_floor_material)} />
                <DetailRow label="Shade level" value={humanizeValue(place.shade_level)} />
                {BEACH_BOOLEAN_FACTS.map((field) => (
                  <DetailRow key={field} label={titleize(field)} value={booleanValue(place[field])} />
                ))}
              </DetailList>
            </FactGroup>
          )}

          <FactGroup title="Facilities and access">
            <DetailList>
              {GENERAL_BOOLEAN_FACTS.map((field) => (
                <DetailRow key={field} label={titleize(field)} value={booleanValue(place[field])} />
              ))}
            </DetailList>
          </FactGroup>

          <FactGroup title="Visitor notes">
            <DetailList>
              <DetailRow label="Entry fee note EN" value={place.entry_fee_note_en} multiline />
              <DetailRow label="Entry fee note DE" value={place.entry_fee_note_de} multiline />
              <DetailRow label="Seasonal note EN" value={place.seasonal_note_en} multiline />
              <DetailRow label="Seasonal note DE" value={place.seasonal_note_de} multiline />
            </DetailList>
          </FactGroup>

          <FactGroup title="Opening hours">
            <DetailList>
              {OPENING_HOURS_DAYS.map((day) => (
                <DetailRow key={day} label={titleize(day)} value={openingHour(place.opening_hours, day)} />
              ))}
            </DetailList>
          </FactGroup>
        </Section>

        <PhotoManager photoManager={photo_manager} />
        <VerificationQuickAdd verificationForm={verification_form} />
      </AdminShell>
    </>
  )
}

function Section({ title, description, children }: { title: string; description?: string; children: ReactNode }) {
  return (
    <section className="mt-8 border-t border-hairline pt-7 first:border-t-0 first:pt-0">
      <h2>{title}</h2>
      {description && <p className="mt-1 text-sm text-ink-muted">{description}</p>}
      <div className="mt-4">{children}</div>
    </section>
  )
}

function LocalizedContent({
  locale,
  name,
  slug,
  summary,
  body,
}: {
  locale: string
  name: string | null
  slug: string | null
  summary: string | null
  body: string | null
}) {
  return (
    <section className="rounded-md border border-hairline p-4">
      <h3>{locale}</h3>
      <DetailList className="mt-4">
        <DetailRow label="Name" value={name} />
        <DetailRow label="Slug" value={slug} />
        <DetailRow label="Summary" value={summary} multiline />
        <DetailRow label="Body" value={body} multiline />
      </DetailList>
    </section>
  )
}

function FactGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mt-5 rounded-md border border-hairline p-4 first:mt-0">
      <h3>{title}</h3>
      <div className="mt-4">{children}</div>
    </section>
  )
}

function DetailList({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <dl className={`grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-[minmax(9rem,max-content)_1fr] ${className}`}>{children}</dl>
}

function DetailRow({ label, value, multiline = false }: { label: string; value: ReactNode; multiline?: boolean }) {
  const missing = value === null || value === undefined || value === ""

  return (
    <>
      <dt className="text-sm text-ink-muted">{label}</dt>
      <dd className={`min-w-0 break-words text-sm ${missing ? "text-ink-muted" : "text-ink-body"} ${multiline ? "max-w-[75ch] whitespace-pre-wrap" : ""}`}>
        {missing ? "Unknown" : value}
      </dd>
    </>
  )
}

function ExternalUrl({ url }: { url: string | null }) {
  if (!url) return null

  return (
    <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1">
      <span className="break-all">{url}</span>
      <ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden />
    </a>
  )
}

function openingHour(hours: Record<string, unknown>, day: string) {
  const value = hours?.[day]
  return typeof value === "string" && value.trim() ? value : null
}

function booleanValue(value: boolean | null) {
  if (value === null) return null
  return value ? "Yes" : "No"
}

function humanizeValue(value: string | null) {
  return value ? titleize(value) : null
}

function titleize(value: string) {
  return value.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase())
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}
